import { Button } from "@/components";
import { useNavigationError } from "@/hooks/useNavigationError";
import { ErrorResponse } from "@/types/errorResponse";
import { axiosAccountInstance } from "@/utils/axios";
import { formatRequestDateTime } from "@/utils/formatter";
import getPortalType from "@/utils/getPortalType";
import axios, { AxiosError } from "axios";
import { format } from "date-fns";
import { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  startAt: Date;
  endAt: Date | null;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

export const DownloadSummaryPDFButton: FC<Props> = ({ startAt, endAt, setIsLoading }) => {
  const { t } = useTranslation();
  const { navigateError } = useNavigationError();

  const handleErrors = (error: AxiosError<ErrorResponse>) => {
    if (axios.isAxiosError(error)) {
      navigateError({
        error: error,
        endpoint: "/api/point/transfer/summary",
        appName: "POINT_TRANSFER_SUMMARY",
        path: "points",
      });
    }
  };

  const downloadPDFBase64 = (pdfBase64: string) => {
    // Base64文字列をデコード
    const binaryString = window.atob(pdfBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Blobを作成してURLを生成
    const blob = new Blob([bytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;

    // ファイル名を設定
    const portalType = getPortalType();
    const titlePrefix =
      portalType === "crew" ? t("CREW_ANNUAL_TRANSFER_PDF_TITLE") : t("DRIVER_ANNUAL_TRANSFER_PDF_TITLE");
    const start = format(startAt, "yyyyMMdd");
    const end = endAt ? format(endAt, "yyyyMMdd") : format(new Date(), "yyyyMMdd");
    link.download = `${titlePrefix}_${start}-${end}.pdf`;

    // リンクを一時的にDOMに追加してクリックイベントを発火させる
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleGetPDFDownload = async () => {
    try {
      setIsLoading(true);
      const response = await axiosAccountInstance.get(`/api/point/transfer/summary/pdf`, {
        params: {
          start_at: formatRequestDateTime(startAt),
          ...(endAt && { end_at: formatRequestDateTime(endAt) }),
        },
      });

      downloadPDFBase64(response.data.pdf);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        handleErrors(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      sx={{
        order: { xs: 0, sm: 1 },
        width: "100%",
        borderRadius: "1000px",
        height: "56px",
      }}
      onClick={handleGetPDFDownload}
    >
      {t("DOWNLOAD_PDF")}
    </Button>
  );
};
